<template>
  <div
    class="leave-encashment-report"
    :class="{
      'is-cash-report': isCashReport,
      'is-bank-report': isBankReport,
      'is-summary-report': isSummaryReport,
    }"
  >
    <div class="no-print control-bar">
      <div class="d-flex align-items-center">
        <h4 class="mb-0">{{ reportTitle }}</h4>
        <b-badge v-show="!loading" variant="light-success" class="ml-2">
          {{ reportBadgeText }}
        </b-badge>
      </div>
      <div class="action-buttons">
        <button class="btn-modern btn-print" @click="print">
          <feather-icon icon="PrinterIcon" size="18" class="mr-50" />
          <span>Print Report</span>
        </button>
        <button class="btn-modern btn-close" @click="close">
          <feather-icon icon="XIcon" size="18" class="mr-50" />
          <span>Close</span>
        </button>
      </div>
    </div>

    <div v-if="loading" class="text-center py-5">
      <b-spinner variant="primary" style="width: 3.5rem; height: 3.5rem" />
      <p class="mt-3 font-weight-bold">Loading leave encashment records...</p>
    </div>

    <div v-else-if="error" class="text-center py-5">
      <feather-icon icon="AlertCircleIcon" size="48" class="text-danger mb-3" />
      <h4 class="text-danger font-weight-bold">{{ error }}</h4>
      <b-button variant="primary" @click="loadReportData" class="mt-3"
        >Retry</b-button
      >
    </div>

    <!-- Cash Report Format -->
    <div v-else-if="isCashReport" class="report-page cash-report-page">
      <div class="cash-report-header">
        <img v-if="schoolLogo" :src="schoolLogo" alt="Logo" class="cash-logo" />
        <h1 class="cash-school-name">{{ displayCampusName }}</h1>
      </div>

      <p class="cash-report-intro">{{ cashReportIntro }}</p>

      <div v-if="rows.length === 0" class="text-center py-4 no-data">
        <p>No leave encashment records found for the selected criteria.</p>
      </div>

      <table v-else class="cash-encashment-table">
        <thead>
          <tr>
            <th class="c-sno">S.No</th>
            <th class="c-id">ID</th>
            <th class="c-name">NAME</th>
            <th class="c-amt">AMOUNT</th>
          </tr>
        </thead>
        <tbody>
          <tr
            v-for="(row, index) in rows"
            :key="`${row.id}-${row.staffID}-${index}`"
          >
            <td class="text-center">{{ index + 1 }}</td>
            <td class="text-center">{{ staffId(row) }}</td>
            <td class="name-cell">{{ (row.name || "—").toUpperCase() }}</td>
            <td class="text-right amt-cell">
              {{ formatCurrency(row.encashmentAmount) }}
            </td>
          </tr>
        </tbody>
      </table>
    </div>

    <!-- Bank Report Format — one page per department -->
    <div v-else-if="isSummaryReport" class="report-page summary-report-page">
      <div class="summary-header">
        <img
          v-if="schoolLogo"
          :src="schoolLogo"
          alt="Logo"
          class="summary-logo"
        />
        <div>
          <h1 class="summary-school-name">{{ displayCampusName }}</h1>
          <p class="summary-subtitle">Leave Encashment Summary Report</p>
          <p class="summary-session" v-if="summarySessionLabel">
            Session: {{ summarySessionLabel }}
          </p>
        </div>
      </div>

      <div v-if="summaryDepartments.length === 0" class="text-center py-4 no-data">
        <p>No leave encashment summary records found for the selected session.</p>
      </div>

      <table v-else class="summary-encashment-table">
        <thead>
          <tr>
            <th class="c-sno">S.No</th>
            <th class="c-dept">Department</th>
            <th class="c-cnt">Staff Count</th>
            <th class="c-cnt">Record Count</th>
            <th class="c-amt">Encashment Amount</th>
          </tr>
        </thead>
        <tbody>
          <tr
            v-for="(row, index) in summaryDepartments"
            :key="`${row.departmentID || 'dept'}-${index}`"
          >
            <td class="text-center">{{ index + 1 }}</td>
            <td>{{ row.departmentName || "—" }}</td>
            <td class="text-center">{{ row.staffCount || 0 }}</td>
            <td class="text-center">{{ row.recordCount || 0 }}</td>
            <td class="text-right amt-cell">
              {{ formatCurrency(row.totalEncashmentAmount) }}
            </td>
          </tr>
          <tr class="summary-total-row">
            <td colspan="2">Grand Total</td>
            <td class="text-center">{{ summaryGrandTotal.staffCount || 0 }}</td>
            <td class="text-center">{{ summaryGrandTotal.recordCount || 0 }}</td>
            <td class="text-right amt-cell">
              {{ formatCurrency(summaryGrandTotal.totalEncashmentAmount) }}
            </td>
          </tr>
        </tbody>
      </table>
    </div>

    <div v-else class="bank-report-wrapper">
      <div
        v-if="rows.length === 0"
        class="report-page bank-report-page text-center py-4 no-data"
      >
        <p>No leave encashment records found for the selected criteria.</p>
      </div>

      <div
        v-for="(group, groupIndex) in bankReportGroups"
        :key="'bank-page-' + groupIndex"
        class="report-page bank-report-page bank-dept-page"
        :class="{ 'bank-dept-page-break': groupIndex > 0 }"
      >
        <div class="bank-letter-top">
          <div class="bank-letter-address">
            <p>The Manager,</p>
            <p>Bank Al-Habib,</p>
            <p>Karachi</p>
          </div>
          <div class="bank-letter-date">
            <strong>Date:</strong> {{ bankReportDate }}
          </div>
        </div>

        <p class="bank-salutation">Respected Sir,</p>

        <p class="bank-letter-body">{{ bankLetterBody }}</p>

        <table class="bank-encashment-table">
          <thead>
            <tr>
              <th colspan="5" class="bank-section-title">{{ group.title }}</th>
            </tr>
            <tr>
              <th class="c-sno">S.No</th>
              <th class="c-id">ID</th>
              <th class="c-title">NAME / TITLE OF ACCOUNT</th>
              <th class="c-acct">ACCOUNT NUMBER</th>
              <th class="c-amt">AMOUNT</th>
            </tr>
          </thead>
          <tbody>
            <tr
              v-for="(row, index) in group.rows"
              :key="`${group.title}-${row.id}-${row.staffID}-${index}`"
            >
              <td class="text-center">{{ index + 1 }}</td>
              <td class="text-center">{{ staffId(row) }}</td>
              <td class="title-cell">{{ accountTitle(row) }}</td>
              <td class="acct-cell">{{ accountNumber(row) }}</td>
              <td class="text-right amt-cell">
                {{ formatCurrency(row.encashmentAmount) }}
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>
  </div>
</template>

<script>
import { BButton, BSpinner, BBadge } from "bootstrap-vue";
import axios from "axios";

export default {
  name: "LeaveEncashmentReportPrint",
  components: {
    BButton,
    BSpinner,
    BBadge,
  },
  data() {
    return {
      loading: false,
      error: null,
      rows: [],
      summaryDepartments: [],
      summaryGrandTotal: {},
      campusName: "MY SKOOL",
      campusBranch: "",
      schoolLogo: null,
      db: "",
      cID: "",
      sessionID: 0,
      depID: 0,
      accountID: "",
      reportType: "bank",
      bankNo: "",
      sessionLabel: "",
      deptLabel: "All Departments",
      reportMonth: null,
      reportYear: null,
      summarySession: "",
      originalPageTitle: "",
    };
  },
  computed: {
    isCashReport() {
      return this.reportType === "cash";
    },
    isBankReport() {
      return this.reportType === "bank";
    },
    isSummaryReport() {
      return this.reportType === "summary";
    },
    reportBadgeText() {
      if (this.isSummaryReport) {
        return `${this.summaryDepartments.length} Departments`;
      }
      return `${this.rows.length} Staff`;
    },
    displayCampusName() {
      return this.campusName || "MY SKOOL";
    },
    reportTitle() {
      const type =
        this.reportType === "cash"
          ? "Cash Report"
          : this.reportType === "bank"
          ? "Bank Report"
          : this.reportType === "summary"
          ? "Summary Report"
          : "Report";
      return `Leave Encashment — ${type}`;
    },
    summarySessionLabel() {
      return this.summarySession || this.sessionLabel || "";
    },
    reportMonthName() {
      const month = Number(this.reportMonth);
      if (!month || month < 1 || month > 12) {
        return new Date().toLocaleString("en-US", { month: "long" });
      }
      return new Date(2000, month - 1, 1).toLocaleString("en-US", {
        month: "long",
      });
    },
    reportYearLabel() {
      const year = Number(this.reportYear);
      return year > 0 ? year : new Date().getFullYear();
    },
    cashReportIntro() {
      return `Following are the leave encashment payments of ${this.displayCampusName} for the month of ${this.reportMonthName} ${this.reportYearLabel} issued by Cheque or Cash.`;
    },
    bankReportDate() {
      const now = new Date();
      const y = now.getFullYear();
      const m = String(now.getMonth() + 1).padStart(2, "0");
      const d = String(now.getDate()).padStart(2, "0");
      return `${y}-${m}-${d}`;
    },
    bankLetterBody() {
      return `Kindly process the leave encashment of ${this.displayCampusName} for the month of ${this.reportMonthName} ${this.reportYearLabel}. Below is the list of individuals, their Bank Al-Habib account number and the corresponding amounts to be credited to their accounts by debiting our bank account # ${this.bankNo}.`;
    },
    bankReportGroups() {
      if (!this.rows.length) return [];

      const sortRows = (list) =>
        [...list].sort((a, b) => {
          const idA = Number(a.rfID || a.staffID) || 0;
          const idB = Number(b.rfID || b.staffID) || 0;
          if (idA !== idB) return idA - idB;
          return String(a.name || "").localeCompare(String(b.name || ""));
        });

      const hasSingleDept =
        this.depID &&
        Number(this.depID) !== 0 &&
        this.deptLabel &&
        this.deptLabel !== "All Departments";

      if (hasSingleDept) {
        return [
          {
            title: String(this.deptLabel).toUpperCase(),
            rows: sortRows(this.rows),
          },
        ];
      }

      const groups = {};
      this.rows.forEach((row) => {
        const title = (this.departmentName(row) || "STAFF").toUpperCase();
        if (!groups[title]) groups[title] = [];
        groups[title].push(row);
      });

      return Object.keys(groups)
        .sort()
        .map((title) => ({
          title,
          rows: sortRows(groups[title]),
        }));
    },
    totalAmount() {
      return this.rows.reduce(
        (sum, row) => sum + (Number(row.encashmentAmount) || 0),
        0
      );
    },
  },
  created() {
    this.originalPageTitle = document.title;
    document.title = " ";
    this.initReport();
  },
  beforeDestroy() {
    if (this.originalPageTitle) {
      document.title = this.originalPageTitle;
    }
  },
  methods: {
    initReport() {
      const query = this.$route.query;
      this.db = query.db || this.$store.state.userData.db;
      this.cID = query.cID || query.cId || this.$store.state.userData.cId || 0;
      this.sessionID = Number(query.sessionID) || 0;
      this.depID = Number(query.depID) || 0;
      this.accountID = query.accountID || "";
      this.reportType = query.reportType || "bank";
      this.bankNo = query.bankNo || "";
      this.sessionLabel = query.sessionLabel || "";
      this.deptLabel = query.deptLabel || "All Departments";
      this.reportMonth = query.reportMonth || null;
      this.reportYear = query.reportYear || null;

      if (!this.db) {
        this.error = "Database identifier is missing. Please close and try again.";
        return;
      }
      if (!this.sessionID) {
        this.error = "Session is required. Please close and try again.";
        return;
      }
      if (!this.isSummaryReport && !this.accountID) {
        this.error = "Account filter is missing. Please close and try again.";
        return;
      }
      if (this.reportType === "bank" && !String(this.bankNo).trim()) {
        this.error = "Bank number is required. Please close and try again.";
        return;
      }

      this.loadCampusInfo();
      this.loadReportData();
    },
    staffId(row) {
      return row.rfID || row.staffID || "—";
    },
    accountTitle(row) {
      const title = row.staffAccountTitle || row.name || "—";
      return String(title).toUpperCase();
    },
    accountNumber(row) {
      const num = row.staffAccountNumber;
      if (!num) return "—";
      return String(num);
    },
    departmentName(row) {
      if (row.department && row.department.name) return row.department.name;
      if (typeof row.department === "string") return row.department;
      return "—";
    },
    async loadCampusInfo() {
      try {
        const url = `${this.$store.state.domain}campuses`;
        const response = await axios.get(url, {
          params: { db: this.db, cId: this.cID },
          headers: {
            Authorization: `Bearer ${this.$store.state.userData.token}`,
            accept: "application/json",
          },
        });

        if (
          response.data &&
          response.data.status === "success" &&
          Array.isArray(response.data.data)
        ) {
          const match = response.data.data.find((c) => c.id == this.cID);
          if (match) {
            this.campusName = match.name;
            this.campusBranch = this.cID == 0 ? "All Campuses" : match.branch;
            this.schoolLogo = match.logo;
          }
        }
      } catch (e) {
        const currentBranch = this.$store.state.currentBranch;
        if (currentBranch) {
          this.campusName =
            currentBranch.organizationName ||
            currentBranch.branch ||
            "MY SKOOL";
          this.campusBranch = currentBranch.branch || "";
        }
      }
    },
    async loadReportData() {
      this.loading = true;
      this.error = null;
      this.rows = [];
      this.summaryDepartments = [];
      this.summaryGrandTotal = {};
      this.summarySession = "";

      try {
        const isSummary = this.isSummaryReport;
        const url = isSummary
          ? `${this.$store.state.domain}StaffLeaveEncashment/ReportSummary`
          : `${this.$store.state.domain}StaffLeaveEncashment/Report`;
        const params = isSummary
          ? {
              db: this.db,
              cID: this.cID,
              sessionID: this.sessionID,
            }
          : {
              db: this.db,
              cID: this.cID,
              sessionID: this.sessionID,
              depID: this.depID || 0,
              accountID: this.accountID,
            };
        const response = await axios.get(url, {
          params,
          headers: {
            Authorization: `Bearer ${this.$store.state.userData.token}`,
            accept: "application/json",
          },
        });

        if (response.data && response.data.status === "success") {
          if (isSummary) {
            const payload = response.data.data || {};
            const departments = Array.isArray(payload.departments)
              ? payload.departments
              : [];
            this.summaryDepartments = departments
              .map((row) => ({
                ...row,
                staffCount: Number(row.staffCount) || 0,
                recordCount: Number(row.recordCount) || 0,
                totalLeavesEncashed: Number(row.totalLeavesEncashed) || 0,
                totalEncashmentAmount: Number(row.totalEncashmentAmount) || 0,
              }))
              .sort((a, b) =>
                String(a.departmentName || "").localeCompare(
                  String(b.departmentName || "")
                )
              );
            this.summaryGrandTotal = {
              staffCount: Number(payload.grandTotal?.staffCount) || 0,
              recordCount: Number(payload.grandTotal?.recordCount) || 0,
              totalLeavesEncashed:
                Number(payload.grandTotal?.totalLeavesEncashed) || 0,
              totalEncashmentAmount:
                Number(payload.grandTotal?.totalEncashmentAmount) || 0,
            };
            this.summarySession = payload.session || this.sessionLabel || "";
          } else {
            const list = Array.isArray(response.data.data)
              ? response.data.data
              : [];
            this.rows = list.sort((a, b) => {
              const idA = Number(a.rfID || a.staffID) || 0;
              const idB = Number(b.rfID || b.staffID) || 0;
              if (idA !== idB) return idA - idB;
              return String(a.name || "").localeCompare(String(b.name || ""));
            });
          }
        } else {
          this.error =
            (response.data && response.data.message) ||
            "Failed to load report data.";
        }
      } catch (err) {
        console.error("Leave encashment report error:", err);
        this.error = "Failed to retrieve report data. Please try again.";
      } finally {
        this.loading = false;
      }
    },
    formatCurrency(amount) {
      const num = Number(amount) || 0;
      return num.toLocaleString("en-PK", {
        minimumFractionDigits: 0,
        maximumFractionDigits: 0,
      });
    },
    print() {
      const previousTitle = document.title;
      document.title = " ";
      const restoreTitle = () => {
        document.title = this.originalPageTitle || previousTitle;
        window.removeEventListener("afterprint", restoreTitle);
      };
      window.addEventListener("afterprint", restoreTitle);
      window.print();
    },
    close() {
      window.close();
    },
  },
};
</script>

<style lang="scss" scoped>
.leave-encashment-report {
  background: #eef1f5;
  min-height: 100vh;
  padding: 1.25rem;
  font-family: Arial, "Segoe UI", sans-serif;
  color: #111;
  font-size: 15px;

  &.is-cash-report,
  &.is-bank-report {
    font-family: "Times New Roman", Times, serif;
  }
}

.control-bar {
  background: #fff;
  padding: 0.85rem 1.5rem;
  margin-bottom: 1.25rem;
  border-radius: 8px;
  display: flex;
  justify-content: space-between;
  align-items: center;
  position: sticky;
  top: 0;
  z-index: 100;
  box-shadow: 0 1px 4px rgba(0, 0, 0, 0.08);

  h4 {
    font-size: 1.3rem;
    font-weight: 700;
  }
}

.btn-modern {
  padding: 7px 16px;
  border-radius: 5px;
  font-weight: 600;
  cursor: pointer;
  border: none;
  margin-left: 8px;
}

.btn-print {
  background: #4a5568;
  color: #fff;
}

.btn-close {
  background: #fff;
  border: 1px solid #ccc;
}

.report-page {
  background: #fff;
  padding: 18px 22px;
  max-width: 100%;
  border-radius: 8px;
  box-shadow: 0 1px 6px rgba(0, 0, 0, 0.06);
}

/* Cash report layout */
.cash-report-page {
  max-width: 820px;
  margin: 0 auto;
  padding: 28px 32px;
  font-family: "Times New Roman", Times, serif;
}

.cash-report-header {
  display: flex;
  align-items: center;
  gap: 16px;
  margin-bottom: 18px;
}

.cash-logo {
  height: 72px;
  width: auto;
  object-fit: contain;
  flex-shrink: 0;
}

.cash-school-name {
  font-size: 28px;
  font-weight: 700;
  margin: 0;
  line-height: 1.2;
  color: #000;
  font-family: "Times New Roman", Times, serif;
}

.cash-report-intro {
  font-size: 16px;
  line-height: 1.5;
  margin: 0 0 20px;
  color: #000;
}

.cash-encashment-table {
  width: 100%;
  border-collapse: collapse;
  table-layout: fixed;
  font-size: 16px;

  th,
  td {
    border: 1px solid #000;
    padding: 8px 10px;
    vertical-align: middle;
  }

  thead th {
    font-weight: 700;
    text-transform: uppercase;
    text-align: center;
    font-size: 15px;
  }

  .c-sno {
    width: 10%;
  }

  .c-id {
    width: 12%;
  }

  .c-name {
    width: 48%;
    text-align: left;
  }

  .c-amt {
    width: 30%;
    text-align: right;
  }

  .name-cell {
    text-transform: uppercase;
    font-weight: 400;
  }

  .amt-cell {
    font-weight: 400;
    white-space: nowrap;
  }
}

/* Summary report layout */
.summary-report-page {
  max-width: 980px;
  margin: 0 auto;
  padding: 28px 32px;
  font-family: "Times New Roman", Times, serif;
}

.summary-header {
  display: flex;
  align-items: center;
  gap: 14px;
  margin-bottom: 18px;
}

.summary-logo {
  height: 66px;
  width: auto;
  object-fit: contain;
}

.summary-school-name {
  margin: 0;
  font-size: 26px;
  font-weight: 700;
  line-height: 1.2;
}

.summary-subtitle {
  margin: 2px 0 0;
  font-size: 17px;
  font-weight: 600;
}

.summary-session {
  margin: 4px 0 0;
  font-size: 15px;
  font-weight: 600;
}

.summary-encashment-table {
  width: 100%;
  border-collapse: collapse;
  table-layout: fixed;
  font-size: 15px;

  th,
  td {
    border: 1px solid #000;
    padding: 7px 8px;
    vertical-align: middle;
  }

  thead th {
    text-transform: uppercase;
    text-align: center;
    font-size: 13px;
    font-weight: 700;
  }

  .c-sno {
    width: 8%;
  }

  .c-dept {
    width: 28%;
  }

  .c-cnt {
    width: 14%;
  }

  .c-leaves {
    width: 16%;
  }

  .c-amt {
    width: 20%;
    text-align: right;
  }
}

.summary-total-row td {
  font-weight: 700;
}

/* Bank report letter layout */
.bank-report-wrapper {
  width: 100%;
}

.bank-report-page {
  max-width: 900px;
  margin: 0 auto;
  padding: 32px 36px;
  font-family: "Times New Roman", Times, serif;
  color: #000;
}

.bank-dept-page-break {
  page-break-before: always;
  break-before: page;
  margin-top: 2rem;
}

.bank-letter-top {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: 24px;
  margin-bottom: 22px;
}

.bank-letter-address {
  p {
    margin: 0 0 2px;
    font-size: 16px;
    line-height: 1.45;
  }
}

.bank-letter-date {
  font-size: 16px;
  white-space: nowrap;
}

.bank-salutation {
  margin: 0 0 14px;
  font-size: 16px;
}

.bank-letter-body {
  margin: 0 0 22px;
  font-size: 16px;
  line-height: 1.55;
  text-align: justify;
}

.bank-encashment-table {
  width: 100%;
  border-collapse: collapse;
  table-layout: fixed;
  font-size: 15px;

  th,
  td {
    border: 1px solid #000;
    padding: 7px 8px;
    vertical-align: middle;
  }

  .bank-section-title {
    text-align: center;
    font-weight: 700;
    font-size: 15px;
    text-transform: uppercase;
    padding: 8px;
  }

  thead tr:nth-child(2) th {
    font-weight: 700;
    text-transform: uppercase;
    text-align: center;
    font-size: 13px;
  }

  .c-sno { width: 8%; }
  .c-id { width: 10%; }
  .c-title { width: 30%; text-align: left; }
  .c-acct { width: 32%; text-align: center; }
  .c-amt { width: 20%; text-align: right; }

  .title-cell {
    text-transform: uppercase;
  }

  .acct-cell {
    font-size: 14px;
    word-break: break-all;
  }

  .amt-cell {
    white-space: nowrap;
  }
}

.report-header-compact {
  display: none;
}

.compact-table {
  display: none;
}

.no-data p {
  margin: 0;
  color: #666;
  font-size: 15px;
}

@page {
  size: A4 portrait;
  margin: 0;
}

@media print {
  .no-print {
    display: none !important;
  }

  html,
  body {
    background: #fff !important;
    margin: 0 !important;
    padding: 0 !important;
    color: #000 !important;
  }

  .leave-encashment-report {
    background: #fff !important;
    padding: 0 !important;
    min-height: auto !important;
    height: auto !important;
  }

  .report-page,
  .cash-report-page,
  .bank-report-page {
    box-shadow: none !important;
    border-radius: 0 !important;
    max-width: 100% !important;
    width: 100% !important;
    margin: 0 !important;
    padding: 1.2cm 0.8cm !important;
  }

  .bank-dept-page-break {
    page-break-before: always !important;
    break-before: page !important;
    margin-top: 0 !important;
  }

  .bank-report-wrapper .bank-dept-page {
    page-break-inside: avoid;
    break-inside: avoid-page;
  }

  .bank-report-page {
    font-size: 11pt !important;
  }

  .bank-letter-address p,
  .bank-letter-date,
  .bank-salutation,
  .bank-letter-body {
    font-size: 11pt !important;
  }

  .bank-encashment-table {
    font-size: 10.5pt !important;

    th,
    td {
      padding: 4px 6px !important;
      border-color: #000 !important;
    }

    .bank-section-title {
      font-size: 10.5pt !important;
    }

    thead tr:nth-child(2) th {
      font-size: 10pt !important;
    }
  }

  .cash-logo {
    height: 58px !important;
  }

  .cash-school-name {
    font-size: 20pt !important;
  }

  .cash-report-intro {
    font-size: 11pt !important;
    margin-bottom: 14px !important;
  }

  .summary-school-name {
    font-size: 18pt !important;
  }

  .summary-subtitle {
    font-size: 12pt !important;
  }

  .summary-session {
    font-size: 10.5pt !important;
  }

  .summary-encashment-table {
    font-size: 10.5pt !important;

    th,
    td {
      padding: 4px 6px !important;
      border-color: #000 !important;
    }

    thead th {
      font-size: 10pt !important;
    }
  }

  .cash-encashment-table {
    font-size: 11pt !important;

    th,
    td {
      padding: 5px 7px !important;
      border-color: #000 !important;
    }

    thead th {
      font-size: 10.5pt !important;
    }
  }

  thead {
    display: table-header-group;
  }
}
</style>

<style lang="scss">
@page {
  size: A4 portrait;
  margin: 0;
}

@media print {
  html,
  body {
    margin: 0 !important;
    padding: 0 !important;
    background: #fff !important;
  }
}
</style>
